import { useEffect, useRef } from "react";

const FOCUSABLE =
  'a[href],area[href],input:not([disabled]),select:not([disabled]),' +
  'textarea:not([disabled]),button:not([disabled]),[tabindex]:not([tabindex="-1"])';

// Body-scroll lock is refcounted so stacked dialogs don't unlock the page when
// the inner one closes — only the last dialog to unmount restores the scroll.
let openDialogCount = 0;
let savedBodyOverflow = "";

function lockBodyScroll() {
  if (openDialogCount === 0) {
    savedBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  openDialogCount += 1;
}

function unlockBodyScroll() {
  openDialogCount = Math.max(0, openDialogCount - 1);
  if (openDialogCount === 0) {
    document.body.style.overflow = savedBodyOverflow;
  }
}

// Modal accessibility in one place: returns a ref to attach to the dialog panel.
// On mount it locks body scroll, moves focus into the dialog, and traps Tab
// inside it; Escape calls onClose; on unmount it restores body scroll and the
// previously-focused element. Pair with role="dialog" aria-modal on the panel.
//
// opts.escapeCloses (default true): set false when the modal manages Escape
// itself (e.g. peeling internal layers) — it then gets focus-trap + scroll-lock
// + focus-restore without this hook also binding Escape.
export default function useDialog(onClose, { escapeCloses = true } = {}) {
  const ref = useRef(null);
  // Keep the latest values without re-running the mount-once effect each render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const escapeClosesRef = useRef(escapeCloses);
  escapeClosesRef.current = escapeCloses;

  useEffect(() => {
    const node = ref.current;
    const prevActive = document.activeElement;
    lockBodyScroll();

    const focusables = node ? node.querySelectorAll(FOCUSABLE) : [];
    (focusables[0] || node)?.focus?.();

    const onKey = (e) => {
      if (e.key === "Escape" && escapeClosesRef.current) {
        onCloseRef.current?.();
        return;
      }
      if (e.key === "Tab" && node) {
        const items = node.querySelectorAll(FOCUSABLE);
        if (!items.length) {
          e.preventDefault();
          return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      unlockBodyScroll();
      prevActive?.focus?.();
    };
  }, []);

  return ref;
}
